<template>
  <div class="manifesto-lista mt-4">
    <div class="manifesto-lista__header">
      <div>
        <h5 class="manifesto-lista__title">
          Notas encontradas
          <span class="manifesto-lista__count">{{ notas.length }}</span>
        </h5>
        <p class="manifesto-lista__subtitle mb-0">
          Selecione uma nota para manifestar ou baixar o XML.
        </p>
      </div>
      <div class="manifesto-lista__actions">
        <button
          type="button"
          class="btn btn-primary"
          :disabled="carregando || !notaSelecionada"
          @click="$emit('manifestar', notaSelecionada)"
        >
          Manifestar
        </button>
        <button
          type="button"
          class="btn btn-success"
          :disabled="carregando || !podeBaixarXml"
          @click="$emit('baixar', notaSelecionada)"
        >
          Baixar
        </button>
        <button
          type="button"
          class="btn btn-outline-primary"
          :disabled="carregando || consultaBloqueada"
          @click="$emit('consultar-novamente')"
        >
          Consultar novamente
        </button>
      </div>
    </div>
    <div
      class="manifesto-table-wrapper table-responsive"
      v-if="notas && notas.length"
    >
      <b-table
        :items="notas"
        :fields="fields"
        hover
        :tbody-tr-class="rowClass"
        thead-class="manifesto-table-head"
        class="manifesto-table mb-0"
        show-empty
        empty-text="Nenhuma nota encontrada"
        @row-clicked="selecionarNota"
      >
        <template #cell(selecionar)="row">
          <input
            v-model="notaSelecionadaId"
            type="radio"
            name="nota-manifesto-selecionada"
            :value="row.item.id"
            :aria-label="`Selecionar NF-e ${row.item.numero || row.item.chave}`"
          />
        </template>
        <template #cell(numero)="row">
          <strong class="manifesto-table__numero">
            {{ row.item.numero || "-" }}
          </strong>
        </template>
        <template #cell(chave)="row">
          <span class="manifesto-table__chave">
            {{ formatChave(row.item.chave) }}
          </span>
        </template>
        <template #cell(nome_exibicao)="row">
          <div class="manifesto-table__parte">
            <span class="manifesto-table__papel">
              {{ row.item.papel_nome || "Parte" }}
            </span>
            <strong class="manifesto-table__nome">
              {{ row.item.nome_exibicao || "-" }}
            </strong>
            <span
              class="manifesto-table__status"
              :class="statusClass(row.item)"
            >
              {{ row.item.ultimo_evento_descricao || "Sem manifestação" }}
            </span>
            <small
              class="manifesto-table__justificativa"
              v-if="row.item.ultimo_evento_justificativa"
            >
              Justificativa: {{ row.item.ultimo_evento_justificativa }}
            </small>
          </div>
        </template>
        <template #cell(cnpj)="row">
          {{ formatCnpj(row.item.cnpj) }}
        </template>
        <template #cell(ie)="row">
          {{ row.item.ie || "-" }}
        </template>
        <template #cell(emissao)="row">
          {{ formatDate(row.item.emissao) }}
        </template>
        <template #cell(tipo_nf)="row">
          <span
            class="manifesto-table__tipo"
            :class="row.item.tp_nf === '0' ? 'tipo-entrada' : 'tipo-saida'"
          >
            {{ row.item.tipo_nf || "-" }}
          </span>
        </template>
        <template #cell(valor)="row">
          {{ formatMoney(row.item.valor) }}
        </template>
        <template #cell(acoes)="row">
          <div class="manifesto-table__row-actions">
            <button
              type="button"
              class="btn btn-outline-danger"
              :disabled="carregando"
              @click.stop="$emit('excluir', row.item)"
            >
              Excluir
            </button>
          </div>
        </template>
      </b-table>
    </div>
    <div
      class="alert alert-light border manifesto-lista__empty"
      v-else-if="carregando"
    >
      <b-spinner small class="mr-2"></b-spinner>
      Consultando... aguarde o retorno.
    </div>
    <div class="alert alert-light border" v-else-if="consultou">
      Nenhum documento localizado para manifesto.
    </div>
  </div>
</template>

<script>
export default {
  name: "ListManifestoNfe",
  props: {
    notas: {
      type: Array,
      default: () => [],
    },
    consultou: {
      type: Boolean,
      default: false,
    },
    carregando: {
      type: Boolean,
      default: false,
    },
    consultaBloqueada: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      notaSelecionadaId: null,
      fields: [
        {
          key: "selecionar",
          label: "",
          thClass: "col-selecionar",
          tdClass: "col-selecionar text-center",
        },
        {
          key: "numero",
          label: "Nº da NF-e",
          sortable: true,
          thClass: "col-numero",
          tdClass: "col-numero",
        },
        {
          key: "chave",
          label: "Chave da NF-e",
          thClass: "col-chave",
          tdClass: "col-chave",
        },
        {
          key: "nome_exibicao",
          label: "Emitente / Destinatário",
          thClass: "col-parte",
          tdClass: "col-parte",
        },
        { key: "cnpj", label: "CNPJ", thClass: "col-cnpj", tdClass: "col-cnpj" },
        { key: "ie", label: "IE", thClass: "col-ie", tdClass: "col-ie" },
        {
          key: "emissao",
          label: "Emissão",
          sortable: true,
          thClass: "col-emissao",
          tdClass: "col-emissao",
        },
        {
          key: "tipo_nf",
          label: "Tipo NF-e",
          thClass: "col-tipo",
          tdClass: "col-tipo",
        },
        {
          key: "valor",
          label: "Valor",
          thClass: "col-valor text-right",
          tdClass: "col-valor text-right",
        },
        {
          key: "acoes",
          label: "Ações",
          thClass: "col-acoes",
          tdClass: "col-acoes",
        },
      ],
    };
  },
  computed: {
    notaSelecionada() {
      return (
        this.notas.find((nota) => nota.id === this.notaSelecionadaId) || null
      );
    },
    podeBaixarXml() {
      return (
        !!this.notaSelecionada &&
        String(this.notaSelecionada.ultimo_evento) === "210200" &&
        !this.consultaBloqueada
      );
    },
  },
  watch: {
    notas() {
      if (!this.notaSelecionada) {
        this.notaSelecionadaId = null;
      }
    },
  },
  methods: {
    selecionarNota(nota) {
      this.notaSelecionadaId = nota.id;
    },
    rowClass(item) {
      return item && item.id === this.notaSelecionadaId
        ? "manifesto-table__row--selected"
        : "";
    },
    statusClass(nota) {
      if (!nota.ultimo_evento) {
        return "status-pendente";
      }
      return nota.ultimo_evento === "210200"
        ? "status-confirmada"
        : "status-manifestada";
    },
    formatChave(value) {
      const chave = String(value || "").replace(/\s/g, "");
      if (!chave) {
        return "-";
      }
      return chave.match(/.{1,4}/g).join(" ");
    },
    formatCnpj(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 14) {
        return digits.replace(
          /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
          "$1.$2.$3/$4-$5"
        );
      }
      if (digits.length === 11) {
        return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
      }
      return value || "-";
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return Number(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      if (/^\d{2}\/\d{2}\/\d{4}/.test(value)) {
        return value;
      }

      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? value
        : date.toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
    },
  },
};
</script>

<style>
.manifesto-lista {
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 3px 12px rgba(31, 45, 61, 0.06);
  overflow: hidden;
}

.manifesto-lista__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 22px;
  border-bottom: 1px solid #e9edf2;
  background: #fbfcfe;
}

.manifesto-lista__title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 3px;
  color: #243447;
  font-size: 18px;
  font-weight: 600;
}

.manifesto-lista__count {
  min-width: 28px;
  padding: 3px 9px;
  border-radius: 14px;
  background: #e9efff;
  color: #4056b5;
  font-size: 13px;
  text-align: center;
}

.manifesto-lista__subtitle {
  color: #738195;
  font-size: 13px;
}

.manifesto-lista__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.manifesto-lista__actions .btn,
.manifesto-table__row-actions .btn {
  min-height: 36px;
  padding: 7px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
}

.manifesto-table-wrapper {
  max-width: 100%;
}

.manifesto-table {
  min-width: 1580px;
  color: #344054;
  font-size: 14px;
  line-height: 1.45;
}

.manifesto-table thead th {
  padding: 13px 12px;
  border-top: 0;
  border-bottom: 1px solid #dce3eb;
  background: #f3f6fa;
  color: #526173;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  white-space: nowrap;
  vertical-align: middle;
}

.manifesto-table tbody td {
  padding: 15px 12px;
  border-top: 1px solid #edf0f4;
  background: #fff;
  vertical-align: middle;
}

.manifesto-table tbody tr {
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.manifesto-table tbody tr:hover td {
  background: #f8faff;
}

.manifesto-table tbody tr.manifesto-table__row--selected td {
  background: #f0f4ff;
  box-shadow: inset 0 1px 0 #dce5ff, inset 0 -1px 0 #dce5ff;
}

.manifesto-table tbody tr.manifesto-table__row--selected td:first-child {
  box-shadow: inset 4px 0 0 #4056b5, inset 0 1px 0 #dce5ff,
    inset 0 -1px 0 #dce5ff;
}

.manifesto-table .col-selecionar {
  width: 54px;
  min-width: 54px;
}

.manifesto-table .col-numero {
  width: 112px;
  min-width: 112px;
}

.manifesto-table .col-chave {
  width: 365px;
  min-width: 365px;
}

.manifesto-table .col-parte {
  width: 270px;
  min-width: 270px;
}

.manifesto-table .col-cnpj {
  width: 165px;
  min-width: 165px;
  white-space: nowrap;
}

.manifesto-table .col-ie {
  width: 135px;
  min-width: 135px;
  white-space: nowrap;
}

.manifesto-table .col-emissao {
  width: 165px;
  min-width: 165px;
  white-space: nowrap;
}

.manifesto-table .col-tipo {
  width: 115px;
  min-width: 115px;
}

.manifesto-table .col-valor {
  width: 135px;
  min-width: 135px;
  white-space: nowrap;
}

.manifesto-table .col-acoes {
  width: 120px;
  min-width: 120px;
}

.manifesto-table input[type="radio"] {
  width: 17px;
  height: 17px;
  cursor: pointer;
}

.manifesto-table__numero {
  color: #26364a;
  font-size: 14px;
}

.manifesto-table__chave {
  color: #4b5f73;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: 13px;
  letter-spacing: 0.01em;
  white-space: normal;
}

.manifesto-table__parte {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 3px;
}

.manifesto-table__papel {
  color: #7b8797;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.manifesto-table__nome {
  color: #27364a;
  font-size: 14px;
  font-weight: 600;
}

.manifesto-table__status,
.manifesto-table__tipo {
  display: inline-flex;
  align-items: center;
  min-height: 23px;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
}

.status-pendente {
  background: #fff4df;
  color: #98691a;
}

.status-confirmada {
  background: #e6f7ee;
  color: #197647;
}

.status-manifestada {
  background: #eaf0ff;
  color: #4056b5;
}

.tipo-entrada {
  background: #e8f7fb;
  color: #176b7d;
}

.tipo-saida {
  background: #edf0ff;
  color: #4056b5;
}

.manifesto-table__justificativa {
  display: block;
  margin-top: 3px;
  color: #697586;
  font-size: 12px;
}

.manifesto-table__row-actions {
  display: flex;
  white-space: nowrap;
}

@media (max-width: 767.98px) {
  .manifesto-lista__header {
    align-items: flex-start;
    flex-direction: column;
    padding: 16px;
  }

  .manifesto-lista__actions {
    justify-content: flex-start;
    width: 100%;
  }

  .manifesto-lista__actions .btn {
    flex: 1 1 auto;
  }
}
</style>
